/* eslint-disable react/no-unstable-nested-components */

'use client';

import {
  ArticleSection,
  ArticleSectionContent,
  ConclusionBlock,
  IBlogPostResponse,
  ImageBlock,
  ParagraphBlock,
  QuoteBlock,
  SliderBlock,
} from '@sapientpro/sapientpro-data-models';
import { useAtomValue, useSetAtom } from 'jotai';
import React, {
  ReactNode, useEffect, useRef, useState,
} from 'react';
import parse, {
  DOMNode, domToReact, Element, HTMLReactParserOptions,
} from 'html-react-parser';
import classNames from 'classnames';
import Button, { ButtonVariants } from '../../../components/Button';
import clientDeviceSize, { ScreenSize } from '../../../store/deviceScreenSize';
import SeeMoreArticleCTA from './SeeMoreArticleCTA';
import SocialBar from './SocialBar';
import ArticleVideo from './ArticleVideo';
import ArticleBreadcrumbs from './ArticleBreadcrumbs/ArticleBreadcrumbs';
import AuthorCard from '../AuthorCard';
import styles from './Article.module.scss';
import ArticleConclusion from './ArticleConclusion';
import ArticleImage from './ArticleImage';
import ArticleNavigation from './ArticleNavigation';
import { sectionProgress } from './articleState';
import Blockquote from './Blockquote';
import ImagesSlider from './ImagesSlider';
import Paragraph from './Paragraph';
import SectionTitle from './SectionTitle';
import { Theme, theme } from '../../../store/theme';
import ImageByTheme from './ImageByTheme';
import ArticleCard from '../ArticlesList/ArticleCard';
import { PageSections } from '../../../pageBuilder';
import CalculatorCTA, { TImageSide } from '../../../components/CalculatorCTA/CalculatorCTA';
import { isElementNode, splitArticleHtml } from './splitArticleHtml';
import { articleBreadcrumbs, pathTo } from '../../../paths';

const IMAGE_TAG_NAMES = ['img', 'image'];

const articleBuilder = (data: ArticleSectionContent, currentTheme: Theme) => data.map((element, index) => {
  let key = element.type + index;

  switch (element.type) {
    case 'image': {
      const lightImage = (element as ImageBlock)?.data?.image;
      const darkImage = (element as ImageBlock)?.data?.darkImage || lightImage;

      const image = currentTheme === Theme.DARK ? darkImage : lightImage;

      return (
        <ArticleImage
          key={key}
          image={image}
          title={(element as ImageBlock).data.title}
        />
      );
    }
    case 'slider':
      return (
        <ImagesSlider
          key={key}
          images={(element as SliderBlock).data.images}
        />
      );
    case 'quote':
      return (
        <Blockquote
          key={key}
          text={(element as QuoteBlock).data.content}
          author={(element as QuoteBlock).data.author}
        />
      );
    case 'conclusion':
      return (
        <ArticleConclusion
          key={key}
          title={(element as ConclusionBlock).data.title}
          text={(element as ConclusionBlock).data.content}
        />
      );
    default:
      return (
        <Paragraph
          key={key}
          value={(element as ParagraphBlock).data.content}
        />
      );
  }
});

type ArticleProps = {
  data: IBlogPostResponse
};
const Article = ({ data }: ArticleProps) => {
  const deviceSize = useAtomValue<ScreenSize>(clientDeviceSize);
  const appTheme = useAtomValue<Theme>(theme);
  const [activeSection, setActiveSection] = useState<number | null>(null);
  const sectionsRef = useRef<(HTMLElement)[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      sectionsRef.current.forEach(((element: HTMLElement) => {
        if (element?.getBoundingClientRect().top < 0 && element?.getBoundingClientRect().bottom >= 0) {
          activeSection !== Number(element?.dataset?.id) && setActiveSection(Number(element?.dataset?.id));
        }
      }));
    };
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const setSectionProgress = useSetAtom(sectionProgress);

  const calculateActiveSectionProgress = () => {
    if (activeSection) {
      const section = sectionsRef.current?.[activeSection]?.getBoundingClientRect();
      if (section) {
        if (section.top > 0) return 0;
        if (Math.abs(section.top) >= section.height) return 1;
        return Math.abs(section.top) / section.height;
      }
    }
  };

  const updateActiveSectionProgress = () => {
    setSectionProgress(calculateActiveSectionProgress() || 0);
  };

  useEffect(() => {
    window.addEventListener('scroll', updateActiveSectionProgress);
    setSectionProgress(0);

    return () => window.removeEventListener('scroll', updateActiveSectionProgress);
  }, [activeSection]);

  const onActiveSectionChange = (id: number) => {
    sectionsRef.current[id].scrollIntoView({ behavior: 'smooth' });
  };

  const breadcrumbs = articleBreadcrumbs(data.post);

  const publishDate = new Date(data.post.publishDate);
  const formattedPublishDate = publishDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const updatedDate = data.post.updated ? new Date(data.post.updated) : null;
  const formattedUpdatedDate = updatedDate ? updatedDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }) : null;

  const articleHtml = splitArticleHtml(data.post.resultHtml);

  const htmlParserOptions: HTMLReactParserOptions = {
    replace: (domNode: DOMNode): false | void | object | Element | null | undefined => {
      if (domNode.type === 'tag' && isElementNode(domNode)) {
        const { attribs } = domNode;

        if (domNode.name === 'p') {
          const content = domNode.children?.map((child) => {
            if (child.type === 'text') {
              return child.data.trim();
            }
            return '';
          }).join('') || '';

          if ((content === '' || content === '\u00A0') && !domNode.children?.some((child) => child.type === 'tag')) {
            domNode.attribs.class = `${domNode.attribs.class || ''}empty-paragraph`;
          }
        }

        if (domNode.name === 'blockquote' && 'text' in attribs) {
          return (
            <Blockquote
              text={attribs.text}
              authorName={attribs.authorname}
              authorPosition={attribs.authorposition}
              authorImage={attribs.authorimage}
            />
          );
        }

        if (domNode.name === 'articlecta' && 'description' in attribs) {
          const media = {
            id: Number(attribs.id),
            url: attribs.image,
            alternativeText: attribs.text,
            width: 100,
            height: 100,
            ext: '',
            caption: '',
          };
          const smallResolutionImageAttr = attribs.smallresolutionimage ? {
            smallResolutionImage: {
              id: Number(attribs.id),
              url: attribs.image,
              alternativeText: attribs.text,
              width: 100,
              height: 100,
              ext: '',
              caption: '',
            },
          } : {};

          return (
            <CalculatorCTA
              data={{
                id: Number(attribs.id),
                __component: PageSections.CTASection,
                title: attribs.text,
                description: attribs.description,
                ctaButton: attribs.buttontext,
                route: attribs.buttonlink,
                imageSide: (attribs.imageside as TImageSide) || 'right',
                media,
                ...smallResolutionImageAttr,
              }}
              articleView
            />
          );
        }

        if (domNode.name === 'imagesslider' && 'items' in attribs) {
          const parsedItems = JSON.parse(attribs.items || '[]');
          return (
            <ImagesSlider
              items={parsedItems}
            />
          );
        }

        if (domNode.name === 'seemorearticlecta' && 'link' in attribs) {
          return (
            <SeeMoreArticleCTA
              text={attribs.text}
              linkText={attribs.linktext}
              link={attribs.link}
            />
          );
        }

        if (domNode.name === 'articlevideo' && 'videoid' in attribs) {
          return (
            <ArticleVideo
              videoId={attribs.videoid}
              videoDescription={attribs.videodescription}
            />
          );
        }

        if (IMAGE_TAG_NAMES.includes(domNode.name) && 'darkimage' in attribs) {
          return (
            <ImageByTheme
              lightImage={attribs.image}
              darkImage={attribs.darkimage}
            />
          );
        }
      }
    },
  };

  const parseHtmlContent = (htmlString: string): ReactNode => parse(htmlString, htmlParserOptions);

  const renderHtmlNodes = (nodes: DOMNode[]): ReactNode => domToReact(nodes, htmlParserOptions);

  let sectionId = 1;

  const oneColumnView = deviceSize < ScreenSize.DESKTOP_SMALL;

  const HighlightText = ({ title }: { title: string }) => {
    const regex = /\*\*(.*?)\*\*/;
    const match = title.match(regex);

    if (match) {
      const before = title.split(match[0])[0];
      const after = title.split(match[0])[1];
      return (
        <span>
          {before}
          <mark>{match[1]}</mark>
          {after}
        </span>
      );
    }
    return <span>{title}</span>;
  };

  return (
    <main className={styles.article}>
      <section className={styles.hero}>
        <div className='container'>
          <ArticleBreadcrumbs items={breadcrumbs} />
          <div className={classNames(styles.titleBlock, 'header1')}>
            <Button
              link={pathTo('blog')}
              variant={ButtonVariants.OUTLINED}
              className={styles.titleButton}
              icon={(
                <svg>
                  <use
                    xlinkHref='/media/arrow-left-bold.svg#arrowLeft'
                    href='/media/arrow-left-bold.svg#arrowLeft'
                  />
                </svg>
              )}
              iconPosition='left'
              iconSize={{ width: 24, height: 24 }}
            >
              All
            </Button>
            {' '}
            <h1 className={styles.title}>
              <HighlightText
                title={data.post.title}
              />
            </h1>
          </div>
          <div className={styles.articleDetails}>
            <p className={styles.articleInfo}>
              {data.post.updated ? (
                <>
                  <span className={styles.articleInfo__updated}>Updated:</span>
                  <span className={styles.articleInfo__date}>{formattedUpdatedDate}</span>
                </>
              ) : (<span className={styles.articleInfo__date}>{formattedPublishDate}</span>)}
              {Boolean(data.post.readTime) && (
                <span>{`${data.post.readTime} min read`}</span>
              )}
            </p>
            <div className={styles.authorBlock}>
              {data.post.author && (
                <AuthorCard
                  url={pathTo('author', { slug: data.post.author.slug })}
                  className={styles.author}
                  name={data.post.author.pseudonym}
                  avatar={data.post.author.avatar?.url || undefined}
                  avatarSize={deviceSize === ScreenSize.DESKTOP_HUGE ? 76 : 48}
                  info={data.post.author.position}
                />
              )}
              {data?.post?.contactFormExpert?.slug && (
                <AuthorCard
                  url={pathTo('author', { slug: data.post.contactFormExpert.slug })}
                  className={styles.contactFormExpert}
                  name={data.post.contactFormExpert.pseudonym}
                  avatar={data.post.contactFormExpert.expertData.avatar?.url || undefined}
                  avatarSize={deviceSize === ScreenSize.DESKTOP_HUGE ? 76 : 48}
                  info={data.post.contactFormExpert.position}
                />
              )}
            </div>
          </div>
          <div className={styles.firstEllipse} />
          <div className={styles.secondEllipse} />
        </div>
      </section>
      <div className='container'>
        <section className={styles.main}>
          <aside className={styles.aside}>
            <div className={styles.asideSticky}>
              {
                oneColumnView && data.post.description && data.post?.content?.some(
                  (section) => section?.__component === 'article.article-section',
                )
                  ? (
                    <div className={classNames(styles.contentStyles, styles.contentStyles__withoutPaddingTopForFirstH2)}>
                      {parseHtmlContent(data.post.description)}
                    </div>
                  ) : oneColumnView && articleHtml.intro.length > 0 && (
                    <div
                      className={classNames(
                        styles.contentStyles,
                        styles.contentStyles__withParagraphBottomPadding,
                        styles.contentStyles__withoutPaddingTopForFirstH2,
                      )}
                    >
                      {renderHtmlNodes(articleHtml.intro)}
                    </div>
                  )
              }
              <ArticleNavigation
                data={data}
                activeSection={activeSection}
                onClick={onActiveSectionChange}
              />
              {(deviceSize > ScreenSize.TABLET_LANDSCAPE) && (
                <SocialBar
                  title={data.post.title}
                />
              )}
            </div>
          </aside>
          <div className={styles.mainContent}>
            <div className={styles.articleContent}>
              {data.post?.content?.some(
                (section) => section?.__component === 'article.article-section',
              ) ? (
                <>
                  {!oneColumnView && data.post.description && (
                    <div className={classNames(styles.contentStyles, styles.contentStyles__withoutPaddingTopForFirstH2)}>
                      {parseHtmlContent(data.post.description)}
                    </div>
                  )}
                  {data.post.content
                    .filter(
                      (section) => section?.__component === 'article.article-section',
                    )
                    .map((section) => (
                      <div
                        className={classNames(
                          styles.contentStyles,
                          { [styles.contentStyles__withoutPaddingTopForFirstH2]: oneColumnView },
                        )}
                        data-id={section.id}
                        key={section.id}
                        ref={(element) => {
                          if (element) sectionsRef.current[+section.id] = element;
                        }}
                      >
                        <SectionTitle value={(section as ArticleSection).heading} />
                        {articleBuilder(
                          (section as ArticleSection).content,
                          appTheme,
                        )}
                      </div>
                    ))}
                </>
                ) : (
                  <>
                    {!oneColumnView && articleHtml.intro.length > 0 && (
                    <div
                      className={classNames(
                        styles.contentStyles,
                        styles.contentStyles__withParagraphBottomPadding,
                        styles.contentStyles__withoutPaddingTopForFirstH2,
                      )}
                    >
                      {renderHtmlNodes(articleHtml.intro)}
                    </div>
                    )}
                    {articleHtml.sections.map((sectionNodes) => {
                      let id = `${sectionId}`;
                      sectionId += 1;
                      return (
                        <div
                          ref={(element) => {
                            if (element) {
                              sectionsRef.current[+id] = element;
                            }
                          }}
                          key={id}
                          className={classNames(
                            styles.contentStyles,
                            styles.contentStyles__withParagraphBottomPadding,
                            { [styles.contentStyles__withoutPaddingTopForFirstH2]: oneColumnView },
                          )}
                          data-id={id}
                        >
                          <div className='h2Wrapper'>
                            {renderHtmlNodes(sectionNodes)}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
            </div>
            {!(deviceSize > ScreenSize.TABLET_LANDSCAPE) && (
              <SocialBar
                title={data.post.title}
              />
            )}
          </div>
        </section>
        <section className={styles.relatedNews}>
          <h3 className={styles.relatedNews__header}>RELATED ARTICLES</h3>
          <div className={styles.relatedNews__content}>
            {data.similarPosts.map(news => (
              <ArticleCard
                key={news.id}
                article={news}
                vertical
                className={styles.relatedNews__item}
              />
            ))}
          </div>
        </section>
      </div>
    </main>
  );
};

export default Article;
